import { AppUserAccountInfo } from "../context";
import { useActiveRegistryDisplayName } from "../hooks/use-active-registry-display-name";
import { AccountType, RegistryCode, TradePosition } from "../types/generated/graphql";
import { TraceXSupportedRegistries } from "../types/okta.types";

type CanAcceptForwardOrderProps = {
  linkedRegistries: AppUserAccountInfo["linkedRegistries"];
  rowRegistryCode?: RegistryCode | null;
  position: TradePosition;
};

export const canAcceptForwardOrder = ({ linkedRegistries, rowRegistryCode, position }: CanAcceptForwardOrderProps) => {
  const isBothRegistriesLinked = linkedRegistries?.length === 2;
  const isERCOTRegistryLinked = Boolean(
    linkedRegistries?.some((registry) => registry.registryCode === RegistryCode.Ercot),
  );
  const isMRETSRegistryLinked = Boolean(
    linkedRegistries?.some((registry) => registry.registryCode === RegistryCode.Mrets),
  );
  const isPositionAsk = position === TradePosition.Ask;
  const isPositionBid = position === TradePosition.Bid;
  const isRowRegistryCodeERCOT = rowRegistryCode === RegistryCode.Ercot;
  const isRowRegistryCodeMRETS = rowRegistryCode === RegistryCode.Mrets;

  const isBothRegistriesNRM =
    isBothRegistriesLinked && linkedRegistries?.every((registry) => registry.accountType === AccountType.NonRegistry);

  const isBothRegistriesRLM =
    isBothRegistriesLinked && linkedRegistries?.every((registry) => registry.accountType === AccountType.Registry);

  const ercotRegistryInfo = isERCOTRegistryLinked
    ? linkedRegistries?.find((registry) => registry.registryCode === RegistryCode.Ercot)
    : null;

  const mretsRegistryInfo = isMRETSRegistryLinked
    ? linkedRegistries?.find((registry) => registry.registryCode === RegistryCode.Mrets)
    : null;

  const { activeRegistryDisplayName } = useActiveRegistryDisplayName(rowRegistryCode as TraceXSupportedRegistries);
  const registryDisplayName = activeRegistryDisplayName || "ANY";

  const validationsMatrix = [
    //BOTH REGISTRIES NRM
    //BID -> Cannot Accept any Order
    //ASK -> Can Accept all Order
    {
      when: () => isBothRegistriesLinked && isBothRegistriesNRM,
      result: {
        canAcceptOrder: !isPositionBid,
        reason: isPositionBid
          ? "You’ve linked an both MRETS & ERCOT Non-Registry account, so BID orders can’t be accepted."
          : "",
      },
    },
    //BOTH REGISTRIES RLM
    //BID -> Can Accept all Order
    //ASK -> Can Accept all Order
    {
      when: () => isBothRegistriesLinked && isBothRegistriesRLM,
      result: {
        canAcceptOrder: true,
        reason: "",
      },
    },
    //ERCOT NRM ONLY
    {
      when: () =>
        !isBothRegistriesLinked && isERCOTRegistryLinked && ercotRegistryInfo?.accountType === AccountType.NonRegistry,
      result: {
        canAcceptOrder: isPositionBid ? false : isPositionAsk && isRowRegistryCodeERCOT,
        reason: (() => {
          if (isPositionBid) return "You’ve linked an ERCOT Non-Registry account, so BID orders can’t be accepted.";
          if (isPositionAsk && isRowRegistryCodeERCOT) return "";
          return `You’ve linked an ERCOT Non-Registry account, so ${registryDisplayName} registry orders can’t be accepted.`;
        })(),
      },
    },
    //ERCOT RLM ONLY
    // BID -> Can Accept ERCOT and ANY Order
    //ASK -> Can Accept ERCOT Order
    {
      when: () =>
        !isBothRegistriesLinked && isERCOTRegistryLinked && ercotRegistryInfo?.accountType === AccountType.Registry,
      result: {
        canAcceptOrder: isPositionBid ? !isRowRegistryCodeMRETS : isRowRegistryCodeERCOT,
        reason: (() => {
          const orderRestrictionMsg = `You’ve only linked an ERCOT Registry account, so ${registryDisplayName} registry orders can’t be accepted.`;

          if (isPositionBid && isRowRegistryCodeMRETS) return orderRestrictionMsg;
          if (isPositionAsk && !isRowRegistryCodeERCOT) return orderRestrictionMsg;
          return "";
        })(),
      },
    },
    //MRETS NRM ONLY
    {
      when: () =>
        !isBothRegistriesLinked && isMRETSRegistryLinked && mretsRegistryInfo?.accountType === AccountType.NonRegistry,
      result: {
        canAcceptOrder: isPositionBid ? false : isRowRegistryCodeMRETS,
        reason: (() => {
          if (isPositionBid) return "You’ve linked an MRETS Non-Registry account, so BID orders can’t be accepted.";
          if (isPositionAsk && isRowRegistryCodeMRETS) return "";
          return `You’ve linked an MRETS Non-Registry account, so ${registryDisplayName} registry orders can’t be accepted.`;
        })(),
      },
    },
    //MRETS RLM ONLY
    // BID -> Can Accept MRETS and ANY Order
    //ASK -> Can Accept MRETS Order
    {
      when: () =>
        !isBothRegistriesLinked && isMRETSRegistryLinked && mretsRegistryInfo?.accountType === AccountType.Registry,
      result: {
        canAcceptOrder: isPositionBid ? !isRowRegistryCodeERCOT : isRowRegistryCodeMRETS,
        reason: (() => {
          const orderRestrictionMsg = `You’ve only linked an MRETS Registry account, so ${registryDisplayName} registry orders can’t be accepted.`;
          if (isPositionBid && isRowRegistryCodeERCOT) return orderRestrictionMsg;
          if (isPositionAsk && !isRowRegistryCodeMRETS) return orderRestrictionMsg;
          return "";
        })(),
      },
    },
    //ERCOT RLM AND MRETS NRM
    //BID -> Can Accept ERCOT and ANY Order
    //ASK -> Can Accept all Order
    {
      when: () =>
        isBothRegistriesLinked &&
        isERCOTRegistryLinked &&
        ercotRegistryInfo?.accountType === AccountType.Registry &&
        isMRETSRegistryLinked &&
        mretsRegistryInfo?.accountType === AccountType.NonRegistry,
      result: {
        canAcceptOrder: isPositionBid ? !isRowRegistryCodeMRETS : true,
        reason: (() => {
          if (isPositionAsk) return "";
          if (isRowRegistryCodeMRETS)
            return `You've linked an ERCOT Registry and MRETS Non-Registry account, so ${registryDisplayName} registry orders can't be accepted.`;
          return "";
        })(),
      },
    },
    //ERCOT NRM AND MRETS RLM
    //BID -> Can Accept MRETS and ANY Order
    //ASK -> Can Accept all Order
    {
      when: () =>
        isBothRegistriesLinked &&
        isERCOTRegistryLinked &&
        ercotRegistryInfo?.accountType === AccountType.NonRegistry &&
        isMRETSRegistryLinked &&
        mretsRegistryInfo?.accountType === AccountType.Registry,
      result: {
        canAcceptOrder: isPositionBid ? !isRowRegistryCodeERCOT : true,
        reason: (() => {
          if (isPositionAsk) return "";
          if (isRowRegistryCodeERCOT)
            return `You've linked an ERCOT Non-Registry and MRETS Registry account, so ${registryDisplayName} registry orders can't be accepted.`;
          return "";
        })(),
      },
    },
  ];

  const matchedRule = validationsMatrix.find((rule) => rule.when());

  return matchedRule?.result ?? { canAcceptOrder: false, reason: "" };
};
